import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { API } from '@/config';
import axios from 'axios';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Check } from 'lucide-react';
import { PLIN_QR_DATA_URI } from '@/lib/plinQr';

const MINUTE_PACKAGES = [
  { key: '1500', minutes: 1500, price: 5, pricePen: 20 },
  { key: '3000', minutes: 3000, price: 10, pricePen: 40 },
  { key: '6000', minutes: 6000, price: 20, pricePen: 75 },
];

const Subscriptions = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [plinOrderInfo, setPlinOrderInfo] = useState(null);
  const [showPlinSubForm, setShowPlinSubForm] = useState(false);
  const [voucherFile, setVoucherFile] = useState(null);
  const [uploadingVoucher, setUploadingVoucher] = useState(false);

  const fetchStatus = async () => {
    if (!token) { setLoading(false); return; }
    try {
      const response = await axios.get(`${API}/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, [token]);

  const handleCancel = async () => {
    if (!window.confirm('¿Cancelar tu suscripción? Mantenés acceso hasta el fin del período ya pagado. Los minutos adicionales que hayas comprado y no uses se pierden al terminar el período — no se pueden recuperar después.')) return;
    try {
      const response = await axios.post(`${API}/subscriptions/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleBuyMinutes = async (packageKey) => {
    try {
      const createRes = await axios.post(`${API}/subscriptions/buy-minutes`, { package: packageKey }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingOrderId(createRes.data.order_id);
      window.open(createRes.data.approve_url, '_blank');
      toast.info('Aprobá el pago en la ventana de PayPal, después volvé acá y confirmá.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar la compra');
    }
  };

  const handlePlinOrder = async (packageKey) => {
    try {
      const response = await axios.post(`${API}/subscriptions/plin-order`, { package: packageKey }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlinOrderInfo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el pedido de Plin');
    }
  };

  const handlePlinSubscriptionOrder = async () => {
    if (!voucherFile) {
      toast.error('Subí una imagen del voucher primero');
      return;
    }
    if (voucherFile.size > 2 * 1024 * 1024) {
      toast.error('La imagen no puede pesar más de 2MB');
      return;
    }
    setUploadingVoucher(true);
    try {
      const voucherDataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(voucherFile);
      });
      await axios.post(`${API}/subscriptions/plin-order`, { type: 'subscription', voucher_image: voucherDataUri }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Pedido enviado. Te avisamos cuando confirmemos tu pago.');
      setVoucherFile(null);
      setShowPlinSubForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el pedido');
    } finally {
      setUploadingVoucher(false);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!pendingOrderId) {
      fetchStatus();
      return;
    }
    try {
      await axios.post(`${API}/subscriptions/capture-minutes`, { order_id: pendingOrderId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('¡Minutos acreditados!');
      setPendingOrderId(null);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar el pago');
      fetchStatus();
    }
  };

  const isSubscriber = ['active', 'cancelled', 'past_due'].includes(status?.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <h1 className="text-4xl font-bold text-amber-900 mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
          Suscripciones
        </h1>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : isSubscriber ? (
          <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-100 p-8 max-w-lg mx-auto">
            <h2 className="text-2xl font-bold text-amber-900 mb-4">Tu suscripción</h2>
            <p className="text-gray-700 mb-2">Plan: <strong>Mensual</strong></p>
            <p className="text-gray-700 mb-2">Minutos usados: <strong>{status.minutes_used.toFixed(0)}</strong></p>
            <p className="text-gray-700 mb-2">Minutos restantes: <strong>{status.minutes_remaining.toFixed(0)}</strong></p>
            {status.minutes_remaining <= 10 && (
              <p className="text-amber-700 bg-amber-50 rounded-lg p-3 my-3 text-sm">
                Te quedan pocos minutos este mes — considerá comprar un paquete adicional abajo.
              </p>
            )}
            <p className="text-gray-500 text-sm mb-6">
              {status.status === 'active' ? 'Renueva' : 'Acceso hasta'}: {new Date(status.current_period_end).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
            </p>
            {status?.status === 'active' && (
              <button onClick={handleCancel} className="text-red-600 hover:text-red-700 text-sm font-medium">
                Cancelar suscripción
              </button>
            )}

            {status?.status === 'active' && (
              <>
                <h3 className="text-lg font-bold text-amber-900 mt-8 mb-4">Comprar minutos adicionales</h3>
                <div className="grid grid-cols-3 gap-3">
                  {MINUTE_PACKAGES.map((pkg) => (
                    <div
                      key={pkg.key}
                      className="border-2 border-amber-200 rounded-xl p-3 text-center"
                    >
                      <p className="font-bold text-amber-900">{pkg.minutes} min</p>
                      <p className="text-sm text-gray-600 mb-2">${pkg.price} / S/ {pkg.pricePen}</p>
                      <button
                        onClick={() => handleBuyMinutes(pkg.key)}
                        className="w-full text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg py-1 mb-1 transition-colors"
                      >
                        Pagar con PayPal
                      </button>
                      <button
                        onClick={() => handlePlinOrder(pkg.key)}
                        className="w-full text-xs bg-blue-100 hover:bg-blue-200 text-blue-900 rounded-lg py-1 transition-colors"
                      >
                        Pagar con Plin
                      </button>
                    </div>
                  ))}
                </div>
                {plinOrderInfo && (
                  <div className="mt-4 p-4 border-2 border-amber-200 rounded-xl text-center max-w-xs mx-auto">
                    <img src={PLIN_QR_DATA_URI} alt="QR de Plin" className="w-64 h-auto mx-auto mb-3" />
                    <p className="font-bold text-amber-900">Pagá S/ {plinOrderInfo.price_pen} con Plin</p>
                    <p className="text-sm text-gray-600 mt-2">
                      Escaneá el código, pagá el monto exacto, y avisanos. Te acreditamos {plinOrderInfo.minutes} minutos en cuanto confirmemos el pago.
                    </p>
                    <button onClick={() => setPlinOrderInfo(null)} className="mt-3 text-sm text-amber-700 underline">
                      Cerrar
                    </button>
                  </div>
                )}
              </>
            )}
            <button onClick={handleConfirmPurchase} className="mt-4 text-sm text-amber-700 underline">
              {pendingOrderId ? 'Ya pagué, confirmar y acreditar minutos' : 'Actualizar estado'}
            </button>
          </div>
        ) : (
          <div className="max-w-sm mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-100 p-6 text-center">
              <h3 className="text-xl font-bold text-amber-900 mb-1">Mensual</h3>
              <p className="text-3xl font-bold text-amber-600 mb-4">$5/mes</p>
              <ul className="text-left text-sm text-gray-600 mb-6 space-y-2">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Fotos y mapa 3D ilimitados</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> 1,500 min de video/mes</li>
              </ul>
              {token ? (
                <>
                  <PayPalScriptProvider options={{ 'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID, vault: true, intent: 'subscription' }}>
                    <PayPalButtons
                      style={{ layout: 'horizontal' }}
                      createSubscription={(data, actions) => {
                        return actions.subscription.create({
                          plan_id: process.env.REACT_APP_PAYPAL_MONTHLY_PLAN_ID,
                        });
                      }}
                      onApprove={async (data) => {
                        try {
                          await axios.post(`${API}/subscriptions/create`, { paypal_subscription_id: data.subscriptionID }, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          toast.success('¡Suscripción activada!');
                          fetchStatus();
                        } catch (error) {
                          toast.error('Error activando la suscripción');
                        }
                      }}
                    />
                  </PayPalScriptProvider>
                  {!showPlinSubForm ? (
                    <button
                      onClick={() => setShowPlinSubForm(true)}
                      className="w-full mt-3 text-sm bg-blue-100 hover:bg-blue-200 text-blue-900 rounded-lg py-2 transition-colors"
                    >
                      Pagar suscripción con Plin (S/ 20)
                    </button>
                  ) : (
                    <div className="mt-4 p-4 border-2 border-amber-200 rounded-xl text-left">
                      <img src={PLIN_QR_DATA_URI} alt="QR de Plin" className="w-64 h-auto mx-auto mb-3" />
                      <p className="text-center font-bold text-amber-900 mb-2">Pagá S/ 20 con Plin</p>
                      <label className="block text-sm text-gray-700 mb-1">Subí tu voucher (imagen, máx 2MB):</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setVoucherFile(e.target.files[0] || null)}
                        className="block w-full text-sm mb-3"
                      />
                      <button
                        onClick={handlePlinSubscriptionOrder}
                        disabled={uploadingVoucher}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg py-2 text-sm font-semibold transition-colors"
                      >
                        {uploadingVoucher ? 'Enviando...' : 'Enviar pedido'}
                      </button>
                      <button onClick={() => { setShowPlinSubForm(false); setVoucherFile(null); }} className="w-full mt-2 text-xs text-gray-500 underline">
                        Cancelar
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => navigate('/auth/login')}
                  className="w-full px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full font-semibold shadow-lg transition-all"
                >
                  Iniciar sesión para suscribirte
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;
